/**
 * ScansByLocation — "Scans by location" section of the Analytics page (VERA-260).
 *
 * Titled to parallel "Scans by time" above it, and deliberately NOT "Activity Map":
 * geo coordinates only ever exist for SCANS, so an "activity" framing promised a
 * dataset this surface cannot show. Broader activity analytics get their own surface.
 *
 * Real geolocated scan data from GET /analytics/admin/scans-map, reusing the
 * certificate-page Scans-tab map pattern (@repo/ui Map + getScanMarkerColor /
 * getScanStatus — see CertificatePage/components/Scans/Scans.tsx). Filter-aware
 * over the same dimensions as the totals/granular endpoints (owner / product /
 * certificate / location / status / date), read from the global analytics
 * filters in redux — same as AnalyticsPage's useAnalyticsTotals call.
 *
 * DATA TYPE — this card's OWN Scans/Certificates control, in its header.
 * -----------------------------------------------------------------------------
 * ⚠️ It used to be a page-level pill shared with the time chart. It scoped only those two
 * cards while sitting beside a genuinely global date range, so it was demoted into each
 * card's header and the two dimensions are now INDEPENDENT — see `AnalyticsFilters` in
 * `store/analytics/types`. This card reads `filters.mapDataType`.
 *
 * In `certificates` mode the map plots CERTIFICATES BY LAST KNOWN LOCATION, which is
 * exactly `mode: 'latest'` on the same endpoint. That is not an approximation: the SQL
 * (`getScanMapPoints` in `analytics.postgres.repository.ts`) partitions the located
 * scans by `certificate_id`, orders each partition by `created_at DESC` and keeps
 * `rn = 1` — one row per certificate, its most recent located scan inside the selected
 * range and filters.
 *
 * Two precision points the copy has to respect, both forced by that SQL:
 *   1. The geo NOT NULL predicate sits INSIDE the partitioned CTE, so `rn = 1` is the
 *      most recent LOCATED scan, not the most recent scan. That is what "last known
 *      location" means, so it is right, but it is not "where it was last scanned".
 *   2. The range bounds the CTE, so this is the last known location WITHIN the selected
 *      dates, and only certificates with at least one located scan in that window appear
 *      at all. The description says "in the selected date range" for that reason.
 *
 * The "All scans" / "Latest location" toggle therefore applies to SCANS MODE ONLY, and
 * hides in certificates mode where the mode is forced (see `ScansByLocationModeToggle`).
 *
 * LIST LEFT, MAP RIGHT — the certificate page's arrangement, not a detail panel.
 * -----------------------------------------------------------------------------
 * A single `ScanItem` detail panel used to render to the left of the map, and ONLY
 * once a marker had been clicked. That gave the card no readable inventory at rest:
 * the pins were the only index of the data, so a brand had to hunt a dot to learn
 * anything, and a clustered pin hid its members entirely. The list is persistent
 * now, selection flows both ways (row click highlights the pin, pin click scrolls to
 * and highlights the row), and the map keeps its tooltip for the hover case.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

import { Stack, useTheme } from '@mui/material'
import { LockIcon } from 'lucide-react'

import type { IScanMapPoint } from '@repo/types'
import {
  getScanStatus,
  getScanTooltipPill,
  type MapDrawMode,
  type MapItem,
  type MapRingPoint,
  type ScanStatusInput,
  type ScanStatusKey,
  type ScanStatusMeta,
} from '@repo/ui'

import { GOOGLE_MAPS_API_KEY } from 'config'
import { useAnalyticsScansMap } from 'hooks/api'
import { setAnalyticsFilters } from 'store/analytics/actions'
import { prepareRing, ringsEqual } from 'utils/geoRing'
import type { AnalyticsDataType, AnalyticsSeriesDimension } from 'store/analytics/types'
import { useDispatch, useSelector } from 'store/hooks'

/*
  VRG-491 — the list | map SPLIT is now one shared component, rendered by this card and by
  the certificate page's Scans tab (§36/§47). Everything it owns used to live in this file:
  the resizer and its px bounds, the collapse rail, the count strip, the skeleton, the
  pins/heat toggle, and the whole no-remount/`relayoutNonce` machinery. The certificate
  page had hand-rolled its own worse copy of the first four and had none of the last, so
  the fixes this card paid six reported bugs for did not reach it. They do now.
*/
import {
  ScanMapPanel,
  SCAN_MAP_PANEL_HEIGHT,
  type ScanMapRenderMode,
} from 'components/display/ScanMapPanel'
/*
  VRG-491 — the draw control itself is shared too, for the same reason and by the same
  §36 argument: the certificate page's Scans tab now arms the same gesture, and the only
  thing the two surfaces do NOT share is where the committed ring is stored (page-global
  redux here, component state there). `ScansByLocationDrawControl` below is what is left
  of this file's copy once that difference is the only difference.
*/
import {
  MapDrawControl,
  type MapDrawControlVariant,
} from 'components/display/MapDrawControl'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
// The SAME pill the filters drawer and the time chart's legend render (§28/§36) — see
// `ScansByLocationLegend` for why this legend stopped hand-rolling its own dot+label row.
import { StatusPill } from 'components/display/StatusPill/StatusPill'
// The SAME scan-outcome catalogue the toolbar control and the drawer field are built from,
// so the legend cannot paint a status a different colour from the chip that filters it.
import { SCAN_STATUS_OPTIONS as SCAN_STATUS_FILTER_OPTIONS } from 'components/analytics/analyticsFilterFields'
/*
  Deep import, NOT the `components/analytics` barrel. That barrel re-exports the card
  registry, which reaches back into this directory — importing it here would close a cycle
  through a module whose evaluation order nothing guarantees. `analyticsFilterFields` above
  is imported deep for the same reason.
*/
import {
  mapDimensionScanStatuses,
  type AnalyticsMapDimension,
} from 'components/analytics/analyticsDisplay'
// The shared name→glyph map. `getScanStatus` NAMES each outcome's glyph but cannot import
// one (it is framework-free and shared by both apps), so the mapping lives app-side and
// there is exactly one of it (§36).
import { ICONS } from 'pages/main/CertificatePage/components/Scans/components/ScanItem'

import { ScanList } from './components'

export type ScansMapMode = 'all' | 'latest'

/**
 * NO `icon` on either option, and that is a decision rather than an omission.
 *
 * `ViewToggle` renders an option as an icon button when it is given a glyph and as a text
 * button when it is not, and the two charts on this page take the icon form because line
 * and bar HAVE conventional glyphs (`ChartLine`, `ChartColumn`) that a reader decodes with
 * no legend. "All scans" and "Latest location" have no such glyph — a pin versus a
 * cluster, say, is a pair we would be inventing, and a reader would have to hover both to
 * learn which is which. That is less legible than the words, not lighter.
 *
 * So this control matches the chart toggles in WEIGHT and not in mechanism: same component,
 * same low-emphasis treatment, words instead of icons because the content demands them.
 */
const MODE_OPTIONS: readonly ViewToggleOption<ScansMapMode>[] = [
  { value: 'all', label: 'All scans' },
  { value: 'latest', label: 'Latest location' },
]

/**
 * The card's title and its one descriptive line (§40). Exported: the shell draws the
 * header, so the page needs them. Same shape as `scansByTimeTitle` /
 * `scansByTimeDescription` next door, and for the same reason: the header text depends
 * on `filters.mapDataType` — THIS card's dimension — which lives in redux and is read by
 * the page.
 *
 * The certificates wording is deliberately narrow. It says "most recent place ... in
 * the selected date range" rather than "current location" or "where it is now", because
 * the query can only ever know the last place a visitor scanned it AND shared a
 * location, inside the selected window. See the SQL note in the file header.
 */
export const scansByLocationTitle = (dataType: AnalyticsMapDimension) =>
  dataType === 'certificates'
    ? 'Certificates by location'
    : dataType === 'invalid_scans'
      ? 'Invalid scans by location'
      : 'Scans by location'

export const scansByLocationDescription = (dataType: AnalyticsMapDimension) =>
  dataType === 'certificates'
    ? 'The most recent place each certificate was scanned in the selected date range. Select a certificate to find it on the map.'
    : dataType === 'invalid_scans'
      ? /*
          ⚠️ THE CAVEAT IS PART OF THE SENTENCE, NOT AN OMISSION FIXED LATER.

          This map draws a SUBSET of the figure on the `Invalid scans` card beside it, for
          two independent reasons, and a description that did not say so would leave a
          reader to explain the gap themselves — which they would do by assuming one of
          the two numbers is wrong. A scan with no shared location is not plotted (true of
          every dimension here), and an invalid scan that matched no certificate at all is
          not returned by this endpoint's `JOIN certificates` — the same population the
          card's own "matched no certificate" note already names.
        */
        'Where scans were rejected as invalid in the selected date range. Only rejected scans that shared a location and matched one of your certificates can be placed, so this is a subset of the Invalid scans total.'
      : 'Where certificates were scanned in the selected date range. Select a scan to find it on the map.'

/**
 * @deprecated Use `scansByLocationTitle(filters.mapDataType)` /
 * `scansByLocationDescription(filters.mapDataType)`.
 *
 * These are the scans-mode strings, kept so an unwired call site still compiles and
 * still reads correctly for the DEFAULT data type. They are wrong in certificates
 * mode: the body plots one point per certificate while the header would still say
 * "Scans by location", which is the header lying about the data beneath it. The page
 * already calls the equivalent functions for the chart card one entry above
 * (`AnalyticsPage.tsx`, `title: scansByTimeTitle(filters.chartDataType)`), so wiring these
 * is the same two lines in the same object.
 */
export const SCANS_BY_LOCATION_TITLE = scansByLocationTitle('scans')
/** @deprecated See `SCANS_BY_LOCATION_TITLE`. Use `scansByLocationDescription(dataType)`. */
export const SCANS_BY_LOCATION_DESCRIPTION = scansByLocationDescription('scans')

/**
 * The card's own trailing header control.
 *
 * Exported as a component because it now renders in `AnalyticsCardShell`'s header
 * action slot, outside this card's body. `mode` is therefore lifted to the page, which
 * is what lets one control drive a body it no longer contains — the alternative was
 * a second `SegmentedTabs` at the page level, i.e. two copies of the same option list.
 *
 * WHY IT HIDES IN CERTIFICATES MODE RATHER THAN DISABLING (§46)
 * ------------------------------------------------------------
 * §46 splits an unavailable control three ways: a PERMISSION block stays visible and
 * disabled with a tooltip, an IN-FLIGHT block disables, and a STATE block hides. This
 * is the third: nothing about the user's permissions or a running request makes the
 * control unavailable, this card's own `mapDataType` does.
 *
 * The axis §46 actually turns on is who owns the resolution. A permission is resolved
 * by someone else, asynchronously, outside the app, so the disabled control is the only
 * channel that can name what to ask for. Here the user resolves it themselves with one
 * click on the Scans/Certificates control they just used, so the disabled row would be
 * telling them something they already know how to undo.
 *
 * §46's forcing function agrees. A disabled item MUST carry a tooltip naming why, and
 * the only sentence available here restates the title of the card ("certificates mode
 * always shows the latest location"), which is the tell that this is state, not
 * permission.
 *
 * It is also stronger than "unavailable". In certificates mode "All scans" is not an
 * option we are withholding, it is a CONTRADICTORY one: selecting it would plot every
 * scan under a title that says one point per certificate. A disabled control implies
 * "this would work if you had access"; that is false.
 *
 * §46's obligation is discharged, and MORE cheaply than it used to be: hiding a
 * state-blocked control requires the RESOLVING action to be reachable in that state, and
 * the Scans / Certificates control is now this card's OWN header control, sitting
 * immediately to the right of where this one was. It used to be a page-level pill above
 * the card — reachable, but a different surface. Now the thing that hides it and the
 * thing that brings it back are adjacent.
 */
export const ScansByLocationModeToggle = ({
  value,
  onChange,
}: {
  value: ScansMapMode
  onChange: (next: ScansMapMode) => void
}) => {
  const dataType = useSelector(state => state.analytics.filters.mapDataType)

  if (dataType === 'certificates') return null

  return (
    /*
      ⚠️ `ViewToggle`, and it has now been through TWO wrong controls — read both before
      changing it a third time.

      (1) A hand-rolled Button group with `grey[900]` fills: a fourth toggle design on a
          platform that already had one, which is what §28 is about.
      (2) `SegmentedTabs`, the house segmented control. Correct by the "reuse what exists"
          rule and wrong by a rule that rule does not encode: the segmented pill is the
          control for switching WHAT IS COUNTED — the page's Scans / Certificates toggle
          changes every figure on screen — while this changes only which points THIS map
          plots. Same chrome, two very different consequences, so the pill overstated it
          and diluted the page control it copied.

      `ViewToggle` is the low-emphasis form: no track, no pill, selected carried by colour
      AND weight. Words rather than icons here, unlike the two charts — see `MODE_OPTIONS`.
    */
    <ViewToggle options={MODE_OPTIONS} value={value} onChange={onChange} ariaLabel="Map points" />
  )
}

/*
  VRG-491 — the render-mode toggle, its low-volume threshold and the split's own layout
  constants moved into `ScanMapPanel` when the split became one shared component. They are
  re-exported here under their original names because nothing is gained by breaking a call
  site to prove a file moved, and because the header-wiring snippet on
  `ScansByLocationDrawControl` still names them.
*/
export type ScansMapRenderMode = ScanMapRenderMode
export {
  HEAT_MODE_MIN_POINTS,
  ScanMapRenderModeToggle as ScansByLocationRenderModeToggle,
} from 'components/display/ScanMapPanel'

/**
 * Minimum container-width change, in px, that justifies REMOUNTING the map.
 *
 * Wider than any platform's scrollbar (~15px macOS, ~17px Windows) so a scrollbar
 * appearing or disappearing can never trigger a teardown, and well under any genuine
 * resize (a split drag, the app sidebar collapsing, a window change), which must still
 * re-tile. See the observer's own note for the bug this closes.
 */
const RESIZE_REMOUNT_MIN_DELTA_PX = 24

/**
 * The scan outcomes this card can actually filter by, in display order.
 *
 * ⚠️ THREE, not the seven in `@repo/ui`'s `SCAN_STATUS_FILTER_OPTIONS`. That list is the
 * full vocabulary `getScanStatus` can return and is right for the certificate page, whose
 * rows carry a whole scan. This card's points are `IScanMapPoint`, and `scanStatusInput`
 * (below) can only hand the resolver `invalid`, `tampered` and `metadata` — `connected`
 * needs a Connect flag and the three `link_*` keys need `render_reason`, neither of which
 * the map endpoint selects.
 *
 * So offering the other four would put options on screen that can never match anything.
 * A filter that always returns nothing is worse than an absent one: it reads as "there are
 * no suspicious links", which is a claim about the DATA, when the truth is that this
 * surface cannot see that field at all.
 *
 * ⚠️ If `scan-map-point.dto.ts` ever grows those fields, widen this — do NOT swap it for
 * the shared seven-item list without re-checking `scanStatusInput` first, or the same dead
 * options come back.
 *
 * Labels track the resolver, deliberately: `tampered` reads "Opened" because an open seal
 * is a fact about the seal and not a failed scan (see `getScanStatus`'s own note). Calling
 * it "Tampered" here would reintroduce the conflation that reframe removed.
 */
const SCAN_STATUS_OPTIONS: ReadonlyArray<{value: ScanStatusKey; label: string}> = [
  {value: 'verified', label: 'Verified'},
  {value: 'invalid', label: 'Invalid'},
  {value: 'tampered', label: 'Opened'},
]

/**
 * The MINIMAL resolver input that produces a given outcome key.
 *
 * The same trick `analyticsFilterFields` uses, and for the same reason: it derives the
 * outcome's own metadata from `getScanStatus` instead of transcribing it, so this file
 * cannot end up describing a status the resolver has since changed. It is also a live
 * assertion that these three inputs still resolve the way this module assumes.
 */
const scanStatusFor = (key: ScanStatusKey): ScanStatusInput =>
  key === 'invalid' ? {invalid: true} : key === 'tampered' ? {tampered: true} : {}

/**
 * The legend row's leading glyph, per outcome and per toggle state.
 *
 * ## It comes from the RESOLVER, not from a map written here
 *
 * `getScanStatus` names each outcome's glyph (`shield-alert` for Invalid, `lock-open` for
 * Opened, and `shield-check` for Verified as of VERA-260) and `ICONS` turns a name into a
 * component. So this function looks the name up rather than choosing a glyph — which is
 * the point: the legend, the scan list beside it and the certificate page's Scans tab all
 * read the same field, so a change to what "Invalid" looks like happens once (§44).
 *
 * ## The ONE state-dependent glyph — the padlock
 *
 * Opened is the exception, deliberately. Its resolver glyph is an OPEN padlock, which is
 * the status; but this row is also a toggle, and a lock has a second, universally read
 * state that costs nothing to use. So a shown Opened row wears the open padlock (its own
 * status glyph, from the resolver) and a hidden one wears a CLOSED padlock — the pill's
 * fill/border/label change is joined by a glyph change, which is a third channel and the
 * one that survives a low-contrast display.
 *
 * The other two do not get an equivalent, and inventing one would be worse than the
 * asymmetry: there is no conventional "off" counterpart to a shield-check or a
 * shield-alert that a reader decodes without being told, and a second shield glyph in the
 * same row would compete with the Invalid/Verified distinction those shields already carry.
 */
const scanStatusGlyph = (key: ScanStatusKey, shown: boolean): React.ReactElement | undefined => {
  // A hidden "Opened" row closes its padlock. `LockIcon` rather than the resolver's
  // `lock-open`, and this is the only place a glyph is chosen rather than resolved.
  if (key === 'tampered' && !shown) return <LockIcon />
  const name = getScanStatus(scanStatusFor(key)).icon
  const Glyph = name ? ICONS[name] : undefined
  return Glyph ? <Glyph /> : undefined
}


/**
 * ── SCAN-OUTCOME VISIBILITY — the map's legend, and its sibling store ─────────────────
 *
 * WHY AN EXTERNAL STORE. Same shape as the chart's `hiddenSeriesStore`, and for the same
 * reason: `<ScansByLocationLegend>` goes into the card shell's header `action` slot and
 * `<ScansByLocation>` into its body, so they are SIBLINGS with no common owner short of
 * `AnalyticsPage`. Lifting state or adding a Context both need that file; an external
 * store needs nothing. Module-private, so toggling provably cannot reach past this card.
 *
 * `getSnapshot` must be referentially stable or `useSyncExternalStore` spins, so `state`
 * is replaced only on a real change.
 */
const EMPTY_HIDDEN: readonly ScanStatusKey[] = []

const hiddenScanStatusStore = (() => {
  let state: readonly ScanStatusKey[] = EMPTY_HIDDEN
  const listeners = new Set<() => void>()
  return {
    subscribe(fn: () => void) {
      listeners.add(fn)
      return () => {
        listeners.delete(fn)
      }
    },
    getSnapshot: () => state,
    toggle(key: ScanStatusKey) {
      state = state.includes(key) ? state.filter(k => k !== key) : [...state, key]
      listeners.forEach(fn => fn())
    },
  }
})()

const useHiddenScanStatuses = () =>
  useSyncExternalStore(hiddenScanStatusStore.subscribe, hiddenScanStatusStore.getSnapshot)

/**
 * The map's legend — the same affordance the chart's legend provides, on the same
 * argument: a legend that only NAMES colours spends header space telling the reader
 * something the pins already say, whereas toggling is the one interaction a reader of a
 * multi-series view actually wants (isolate invalid scans, compare against verified).
 *
 * ## ⚠️ THIS HIDES; IT DOES NOT FILTER. The distinction is the whole design.
 *
 * There is now a REAL `Scan status` filter in the toolbar and the drawer, which narrows
 * the QUERY server-side. This does something deliberately different and weaker: it hides
 * already-fetched points from this card's view. Three reasons it is not wired to the
 * global filter instead:
 *
 *  1. Same rule the chart's legend states — a control inside one card must not silently
 *     rewrite `filters.*`, which scopes the KPI row above this card and everything else
 *     on the page. A page-scoping side effect from clicking a colour swatch is not
 *     something a reader can predict.
 *  2. Hiding is INSTANT. Writing the filter refetches, and the point of clicking a legend
 *     row is to compare what is left against what was there a moment ago.
 *  3. They compose rather than compete: the filter decides what the map is ABOUT, the
 *     legend decides what you are looking at within that. Both writing one value would
 *     make the toolbar chip and the legend fight over the same state.
 *
 * Consequence, stated rather than hidden: the hidden set is NOT part of a saved view, by
 * design — it is ephemeral like card expansion and the chart's own hidden series. The
 * toolbar filter IS persisted, which is the right split.
 *
 * ── IT IS A `StatusPill` ROW NOW, AND IT IS FILTER-AWARE (VERA-260) ──────────────────
 *
 * Two changes, both bringing it onto `ScansByTimeLegend`'s established shape:
 *
 *  1. **The row is a `StatusPill`**, the extracted shared component the filters drawer and
 *     the time chart's legend both render (§28/§36) — not the hand-rolled dot+label button
 *     this used to be. That button had drifted in exactly the way §36 predicts: it was the
 *     one toggle in this epic with no `aria-pressed`, no `:focus-visible` ring and no
 *     keyboard operability, because it was a `Box component="button"` styled from scratch
 *     rather than the pill everything else uses. Adopting the pill fixes all three for
 *     free, and a reader who has learned the drawer's chips reads these the same way.
 *
 *     Its colours come from `SCAN_STATUS_OPTIONS` — the SAME catalogue the toolbar's Scan
 *     status control and the drawer's Scan Status field are built from — so a legend row
 *     and the chip that filters it can never be two different colours (§44).
 *
 *  2. **Only the outcomes the page is FILTERED TO get a row.** Filter to Invalid and the
 *     legend stops offering Verified and Opened, because after that filter the map holds
 *     no verified or opened pins to key or to toggle. Mirrors `ScansByTimeLegend`, which
 *     draws exactly the certificate-status series the filter selected and nothing else.
 *
 * ⚠️ ONE DELIBERATE DIVERGENCE FROM THE CHART LEGEND, reported rather than smuggled:
 * with NO scan-status filter applied this renders ALL THREE rows, where
 * `ScansByTimeLegend` returns `null`. The chart's null is right for the chart and would be
 * wrong here, because the two unfiltered states are not the same thing. Unfiltered, the
 * chart draws ONE brand-purple series that its own title names — a legend would repeat the
 * title, which is that component's stated reason for the null. Unfiltered, this map draws
 * pins in THREE colours at once and offers the only isolate-one-outcome control on the
 * card. Returning null would delete a live key and a live capability in the DEFAULT state,
 * to avoid a repetition that does not exist here. "Only the statuses filtered on" and "all
 * three when nothing is filtered" are the same rule — the legend keys what the map is
 * currently showing — and an empty `scanStatuses` means all three are showing.
 */
export const ScansByLocationLegend = ({
  counts,
}: {
  /** Points per outcome in the CURRENT result set — omit to render counts as blank. */
  counts?: Partial<Record<ScanStatusKey, number>>
}) => {
  const isDark = useTheme().palette.mode === 'dark'
  const hidden = useHiddenScanStatuses()
  /**
   * The page-level filter, read straight from redux.
   *
   * READ ONLY, and the asymmetry is the design: this legend never WRITES `scanStatuses`
   * (see the docblock above on why a card-local control must not rewrite page-scoping
   * state), but it must READ it, or it would go on offering rows for outcomes the server
   * has already excluded from the result set.
   */
  const scanStatuses = useSelector(state => state.analytics.filters.scanStatuses)

  /*
    ⚠️ NOTHING IS RENDERED UNTIL THE FILTER IS SET. This used to fall back to all three
    outcomes when `scanStatuses` was empty, and that read as COUNTERINTUITIVE — which is
    the right word for it.

    Three lit, coloured pills sitting above the map by default do not read as a key. They
    read as three APPLIED FILTERS, because that is exactly what the same pills mean in the
    toolbar and the drawer, and a reader has no way to tell that this instance is passive.
    So the default state was making a claim about scoping that was not true.

    Matching `ScansByTimeLegend`, which returns `null` for the identical reason: unfiltered,
    a legend that names every possible value is not telling you anything you could not read
    off the marks themselves.

    Honest cost, stated because it is real: hide/isolate is this card's only in-place way to
    compare one outcome against another, and it is now unreachable until a filter is set —
    at which point the server has already narrowed the data, so isolating WITHIN a filtered
    set is a weaker version of what the control could do. If that turns out to matter, the
    answer is a dedicated affordance that reads as a view control rather than as a filter,
    NOT reinstating the always-on pills.
  */
  const visible = SCAN_STATUS_OPTIONS.filter(o => scanStatuses.includes(o.value))

  // Nothing filtered, or a filter selecting only outcomes this card cannot resolve.
  if (visible.length === 0) return null

  return (
    <Stack direction="row" flexWrap="wrap" gap={2}>
      {visible.map(({value, label}) => {
        const isHidden = hidden.includes(value)
        /*
          The MARKER's exact colour, from the same resolver the pins use — `getScanStatus`
          with `isDark`, not a second palette lookup. The legend's one job is "which pin is
          which", and only the pin's actual fill can answer it — which is why it is pinned
          onto the GLYPH via `iconColor`, exactly as the chart legend pins its swatch to the
          line's own stroke. It matters most for Opened, whose pin is the verified purple
          (an opened seal is not a failed scan) while its pill is neutral zinc: without the
          pinned glyph nothing on this row would say which pins it stands for.
        */
        const markerColor = getScanStatus(scanStatusFor(value), {isDark}).color
        const option = SCAN_STATUS_FILTER_OPTIONS.find(o => o.value === value)
        const count = counts?.[value]
        return (
          <StatusPill
            key={value}
            option={{
              value,
              // `StatusPill`'s label is one `Typography`, so the count rides in the string
              // rather than as a separately-toned span the way the old hand-rolled row had
              // it. No live caller passes `counts` today (`analyticsDisplayVariants`
              // renders `<ScansByLocationLegend />` bare), so this branch is currently
              // unreached — kept because the prop is part of the component's contract.
              label: count === undefined ? label : `${label} · ${count.toLocaleString()}`,
              // Fall back to the marker hue only if the shared catalogue ever stops
              // carrying this key — it carries all three today.
              color: option?.color ?? markerColor,
              backgroundColor: option?.backgroundColor,
              borderColor: option?.borderColor,
              icon: scanStatusGlyph(value, !isHidden),
            }}
            // Pinned to the PIN's colour while shown; released to the pill's own muted
            // treatment while hidden, so a hidden row does not go on asserting its colour.
            iconColor={isHidden ? undefined : markerColor}
            // Identity mapping, same as the chart legend: SHOWN is SELECTED. In the drawer
            // a selected chip means "this status is IN the query"; here it means "IN the
            // map". Reversing it would make a pill mean the opposite of what the same pill
            // means two controls away.
            selected={!isHidden}
            onClick={() => hiddenScanStatusStore.toggle(value)}
            // Names the THING, not the state — `aria-pressed` carries the state, and
            // announcing both is how a toggle ends up read out contradicting itself.
            ariaLabel={`${label} scans`}
            tooltip={isHidden ? `Show ${label.toLowerCase()} scans` : `Hide ${label.toLowerCase()} scans`}
          />
        )
      })}
    </Stack>
  )
}

/**
 * The DOM id of the map card's body, so a surface elsewhere on the page can bring the map
 * back into view.
 *
 * Exists for the applied-filter chip. `Area: drawn` is the only chip on this page whose
 * editing surface is not the filter drawer — the shape is drawn on the map, so the chip
 * has to return the reader THERE. Without an anchor the chip could only clear the area,
 * which is a strictly worse answer to "what is this filtering, and how do I change it".
 */
export const SCANS_MAP_ANCHOR_ID = 'analytics-scans-map'

/**
 * Scroll the map card into view. Safe to call when the card is not on the canvas — a
 * reader can remove it while an area filter is still applied, and in that state the chip
 * still clears (its X) but has nowhere to send them.
 *
 * @returns `true` when the map was found and scrolled, so a caller can fall back.
 */
export const focusScansMap = (): boolean => {
  const el = typeof document === 'undefined' ? null : document.getElementById(SCANS_MAP_ANCHOR_ID)
  if (!el) return false
  el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  return true
}

/**
 * ── THE AREA-SELECTION CONTROL (VRG-480) ────────────────────────────────────────────
 *
 * The redux-bound wrapper around the shared `MapDrawControl`. Everything visual —
 * the badge, the two dividers, the glyphs, the `X`, the gap arithmetic and the whole
 * history behind them — moved to `components/display/MapDrawControl` in VRG-491, when
 * the certificate page's Scans tab gained the same gesture (§36: the second hand-roll is
 * a shared component). Read that file for the design reasoning; this one is only the
 * wiring, which is the part the two surfaces genuinely do NOT share.
 *
 * ## Why it is not a Layout card
 *
 * It was first raised as "a Draw on map card" and it cannot be one, for the reason this
 * epic has now applied twice: a control that narrows EVERY card on the page must not live
 * inside one removable card. The same argument is why the Scans / Certificates pill was
 * demoted out of the page header and why the ROI hero's period was pulled back out of it.
 *
 * ## Why the drawn AREA is nonetheless page-global while the control is not
 *
 * The two halves answer to different rules and it is worth being explicit, because the
 * arrangement looks contradictory. The GESTURE is direct manipulation — it belongs on the
 * canvas it draws on, and there is nowhere else it could sensibly live. The RESULT is a
 * filter over every figure on the page, and the surface that says so is the applied-filter
 * chip row, which is page-level. So the control is local and the constraint is global, and
 * the chip is what carries the global claim. Putting the control in the map card's HEADER
 * rather than on its canvas does not change that arithmetic — the header is inside the
 * same removable card — which is why `variant` exists rather than the placement being
 * settled here.
 *
 * ⚠️ This wrapper is what makes the ring PAGE-GLOBAL, and that is exactly why it did not
 * come along to the certificate page. `setAnalyticsFilters({ area })` narrows every card
 * on the Analytics canvas at once, which is right there and wrong on a certificate's Scans
 * tab — one tab, one list, one map, and no other consumer of the ring. That tab holds its
 * own ring in component state and renders `MapDrawControl` directly.
 */
export const ScansByLocationDrawControl = ({
  value,
  onChange,
  variant = 'header',
}: {
  value: MapDrawMode
  onChange: (next: MapDrawMode) => void
  variant?: MapDrawControlVariant
}) => {
  const dispatch = useDispatch()
  const area = useSelector(state => state.analytics.filters.area)

  return (
    <MapDrawControl
      value={value}
      onChange={onChange}
      variant={variant}
      hasArea={Boolean(area)}
      onClear={() => dispatch(setAnalyticsFilters({ area: null }))}
    />
  )
}

/*
  The split's height, its list-column width and its resize bounds all live on
  `ScanMapPanel` now, with their reasoning — one home for the numbers means the skeleton,
  the resizer and the map canvas cannot disagree about them on one surface and agree on
  the other. `SCAN_MAP_PANEL_HEIGHT` is the only one this file still needs, because the
  card sizes its own body when it is not expanded.
*/


function escapeHtml(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"]/g,
    c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;'})[c] as string,
  )
}

function scanStatusInput(point: IScanMapPoint): ScanStatusInput {
  return {
    invalid: point.invalid,
    tampered: point.tampered,
    metadata: point.metadata as {tampered?: boolean | null} | null,
  }
}

function locationText(point: IScanMapPoint): string {
  return [point.geo_city, point.geo_state, point.geo_country].filter(Boolean).join(', ')
}

// Reuses the `.vmt-name` / `.vmt-sub` / `.vmt-avatar` classes the shared Map
// component styles per mapStyle (light/dark) — same mechanism the cert-page
// Scans tooltip uses, substituting a product thumbnail for the scanner avatar.
//
// VRG-491: the pill's label + tones come from the shared resolver (`getScanTooltipPill`),
// not a local `STATUS_PILL` table. That table had `tampered` on the same error-red as
// `invalid`, which the resolver stopped asserting when "Opened" was reframed as a neutral
// fact about the seal rather than a failed scan — so the label read "Opened" in red, which
// is the overclaim wearing the corrected word. Verified stays unbadged, now because
// `showChip` says so rather than because it was absent from a hand-written map.
function buildScanTooltip(point: IScanMapPoint, status: ScanStatusMeta): string {
  const pill = getScanTooltipPill(status)
  const pillHtml = pill
    ? `<span style="margin-left:8px;padding:1px 8px;border-radius:999px;font-size:11px;font-weight:600;line-height:1.5;color:${pill.text};background:${pill.fill};border:1px solid ${pill.border};">${pill.label}</span>`
    : ''
  const imgHtml = point.product_image
    ? `<img src="${escapeHtml(point.product_image)}" style="width:32px;height:32px;border-radius:6px;object-fit:cover;flex:none;" />`
    : `<div class="vmt-avatar" style="width:32px;height:32px;border-radius:6px;flex:none;"></div>`
  const loc = locationText(point)
  return (
    `<div style="display:flex;align-items:center;gap:11px;">${imgHtml}` +
    `<div style="display:flex;flex-direction:column;line-height:1.35;">` +
    `<span style="display:flex;align-items:center;"><span class="vmt-name" style="font-size:14px;font-weight:600;">${escapeHtml(point.product_title)}</span>${pillHtml}</span>` +
    (loc ? `<span class="vmt-sub" style="font-size:13px;font-weight:400;">${escapeHtml(loc)}</span>` : '') +
    `</div></div>`
  )
}

interface ScansByLocationProps {
  /**
   * The all/latest mode.
   *
   * Owned by the PAGE, because the control that sets it (`ScansByLocationModeToggle`)
   * renders in the card shell's header — outside this component's tree. State has to
   * live at or above the point where both halves meet, and that is the page.
   */
  mode?: ScansMapMode
  /**
   * PIN this card's dimension instead of following the page's `filters.mapDataType`.
   *
   * The legacy map card is a single instance whose dimension the reader flips with a
   * header toggle, so redux is the right home for it. A DISPLAY VARIANT is not: it was
   * created by dragging the Map affordance off ONE metric's row, so its dimension is part
   * of its identity — a "Certificates by location" card that silently became a scans map
   * because another card's toggle moved would be the header lying about the body beneath
   * it, which is the exact defect the single global `dataType` was split to fix.
   *
   * Undefined keeps the redux behaviour verbatim, so the legacy card is untouched.
   *
   * ⚠️ THE WIDE UNION (VERA-260), not the two-member `AnalyticsDataType` the redux key
   * uses. A dimension reaches this prop only through `analyticsDisplayCapability(...).map`,
   * which is total over the same union and reports `true` for exactly the three members
   * this card can draw — so the prop's type being wider than the capable set is not a hole,
   * it is the same arrangement `dataType` has on the chart. `filters.mapDataType` stays
   * narrow because the header toggle that writes it offers two options.
   */
  dataType?: AnalyticsMapDimension
  /** True when this card owns the whole content region — the split then fills it. */
  expanded?: boolean
  /**
   * The page renders `ScansByLocationDrawControl` in this card's HEADER action slot, so
   * this body must not also float one over the tiles.
   *
   * Defaults to false — i.e. the control appears on the canvas — because that is the only
   * placement this component can guarantee. Moving it to the header is two lines at the
   * page (`<ScansByLocationDrawControl value={drawMode} onChange={setDrawMode} />` in the
   * map card's `action`, and `drawControlInHeader` here), exactly mirroring how `mode`
   * and `ScansByLocationModeToggle` are already lifted.
   *
   * ⚠️ Setting this without ALSO rendering the control in the header leaves a map that
   * can never be drawn on, with a chip elsewhere on the page saying an area is applied.
   */
  drawControlInHeader?: boolean
  /**
   * CONTROLLED pins/heat mode — see `ScansByLocationRenderModeToggle`'s doc for the
   * intended header wiring and why this card falls back to managing it locally when
   * these are absent. Pass BOTH or neither; passing one without the other silently
   * drops writes (the callback fires but nothing reads the result).
   */
  renderMode?: ScansMapRenderMode
  onRenderModeChange?: (next: ScansMapRenderMode) => void
}

export const ScansByLocation = ({
  mode = 'all',
  dataType,
  expanded = false,
  drawControlInHeader = false,
  renderMode: renderModeProp,
  onRenderModeChange,
}: ScansByLocationProps = {}) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  const mapStyle = theme.palette.mode === 'dark' ? 'dark' : 'light'
  const isDark = theme.palette.mode === 'dark'

  const [activeId, setActiveId] = useState<string | undefined>()

  /*
    The inner split's state — collapsed, and the list's width — is LOCAL and ephemeral, and
    now lives in `ScanMapPanel` rather than here.

    It deliberately does NOT go into `TableViewState` with the saved views. That state is
    the per-card GRID layout the canvas owns: which cards are placed, and each one's
    x/y/w/h. How one card arranges its own interior is not part of that contract, and
    putting it there would mean every saved view carried a field only this card can read —
    the same reason the expanded card is ephemeral. If a persisted split is ever wanted,
    the honest home is a per-card settings blob, not the layout array.
  */

  const filters = useSelector(state => state.analytics.filters)
  // THIS CARD's dimension, not a page-level one. See `AnalyticsFilters` in
  // `store/analytics/types` for why the single global `dataType` was split in two.
  // A pinned `dataType` WINS over the page's. See the prop.
  const dimension: AnalyticsMapDimension = dataType ?? filters.mapDataType
  const isCertificates = dimension === 'certificates'

  /**
   * ── THE OUTCOME THIS CARD IS NAMED FOR, INTERSECTED WITH THE PAGE FILTER ────────────
   *
   * `invalid_scans` is drawn by asking the endpoint for the rejected subset
   * (`mapDimensionScanStatuses` — the executable form of `MAP_RENDERABLE`'s claim). The
   * pin is the card's IDENTITY, so neither it nor the page's `Scan status` filter may
   * simply overwrite the other:
   *
   *  - page filter wins  -> "Invalid scans by location" plots verified pins. The card
   *                         becomes a different measurement while keeping its title, which
   *                         is the worst failure available on this page.
   *  - pin wins outright -> the card ignores a filter the toolbar chip says is applied,
   *                         so the page shows a scoping claim that one card does not obey.
   *
   * Intersection is the only reading that is true of both: the card stays what it is named
   * for, and the filter can only narrow it further. Selecting "Verified" therefore empties
   * this card, which is correct — there are no invalid scans that are also verified
   * (`buildScanOutcomeFilter` writes the three outcomes as a true partition).
   *
   * ⚠️ AN EMPTY INTERSECTION MUST NOT BE SENT. `scan_statuses: []` means NO NARROWING to
   * the server, not "nothing" — `buildScanOutcomeFilter` returns `''` for an empty list —
   * so a naive intersection would answer "no invalid scans are verified" by plotting EVERY
   * scan the brand has. `enabled: false` below is what makes the empty case render an empty
   * map instead, and it is the reason this is computed here rather than inside the hook.
   */
  const pinnedScanStatuses = mapDimensionScanStatuses(dimension)
  const effectiveScanStatuses = useMemo(() => {
    if (!pinnedScanStatuses) return undefined
    return filters.scanStatuses.length
      ? pinnedScanStatuses.filter(s => filters.scanStatuses.includes(s))
      : [...pinnedScanStatuses]
  }, [pinnedScanStatuses, filters.scanStatuses])
  /** A pinned card whose pin the page filter has excluded entirely. Ask for nothing. */
  const pinExcludedByFilter = Boolean(
    effectiveScanStatuses && effectiveScanStatuses.length === 0,
  )

  /**
   * Certificates mode FORCES `latest`, which is the query that returns one point per
   * certificate (see the SQL note in the file header). The `mode` prop still carries the
   * user's scans-mode choice so that flipping the header control back to Scans restores
   * whichever of All scans / Latest location they had selected, rather than silently
   * resetting it. The control that sets it is hidden while this is forced, so there is
   * no state the user can see that disagrees with what the map is plotting.
   */
  const effectiveMode: ScansMapMode = isCertificates ? 'latest' : mode

  const {data, isLoading} = useAnalyticsScansMap(
    {
      from: filters.dateRange.from,
      to: filters.dateRange.to,
      owner_ids: filters.ownerIds.length > 0 ? filters.ownerIds : undefined,
      product_ids: filters.productIds.length > 0 ? filters.productIds : undefined,
      certificate_ids: filters.certificateIds.length > 0 ? filters.certificateIds : undefined,
      location: filters.location.length > 0 ? filters.location : undefined,
      certificate_statuses: filters.certificateStatuses.length > 0 ? filters.certificateStatuses : undefined,
      mode: effectiveMode,
      /*
        Present only on a PINNED card. Left `undefined` otherwise so `useAnalyticsScansMap`
        injects the page filter exactly as it always has — `JSON.stringify` drops undefined
        keys, so an unpinned card's cache key is byte-identical to what it was before this
        prop existed and no saved view's map re-requests on upgrade.

        ⚠️ LAST, AFTER `mode`, and the position is load-bearing rather than tidy.
        `analyticsScansMapKey` is `JSON.stringify` over the payload, so property ORDER is
        part of the key — and `withAnalyticsScanStatuses` appends via `{...params,
        scan_statuses}`, i.e. after every key in this literal. Writing it here puts a
        card-supplied value in the exact slot the hook-supplied one occupies, so a pinned
        card and an unpinned card that happen to resolve to the same outcome share ONE
        cache entry instead of keying the identical question twice. Verified live: with the
        page filtered to Invalid, `Scans by location` and `Invalid scans by location` both
        read 21 points from the same entry.

        ⚠️ It does NOT dedupe the REQUEST — `useQuerySelect` compares only its own previous
        params, so two cards asking one question still issue two GETs. That is pre-existing
        and true of any two cards that agree; the shared key is about which ANSWER each one
        reads, which is the half that can be wrong rather than merely wasteful.
      */
      scan_statuses:
        effectiveScanStatuses && effectiveScanStatuses.length > 0
          ? [...effectiveScanStatuses]
          : undefined,
    },
    // See `pinExcludedByFilter` — an empty intersection cannot be expressed as a request.
    { enabled: !pinExcludedByFilter },
  )

  /**
   * FIRST LOAD ONLY (§19) — and here it is more than a flicker.
   *
   * `useQuerySelect` sets `isLoading = true` on EVERY params change, so gating the split
   * on `isLoading` alone replaced the whole card — list AND map — with skeletons on each
   * filter change. Same defect as the chart's (`ScansByTime2`) and the KPI row's, with
   * one extra cost specific to this card: unmounting the split destroys the Google Map,
   * and the map tiles for its MOUNT-TIME box, so every filter change paid a full re-init
   * and re-tile of the basemap.
   *
   * 🚨 `!data` WAS NOT THE HONEST TEST, AND THIS WAS THE DRAW FLASH — reported EIGHT
   * times, survived six fixes, because every one of those governed the camera of a LIVING
   * map and this destroys the map outright.
   *
   * The claim above (kept, struck through, because the reasoning is the lesson) was "the
   * redux slot is written on success and never cleared, so it is true exactly once."
   * Never-cleared is true. Exactly-once is FALSE, because `data` is not one slot — it is
   * a REQUEST-KEYED one:
   *
   *     const key  = analyticsScansMapKey(request)               // useAnalyticsScansMap
   *     const data = useSelector(s => s.analytics.scansMap[key] ?? null)
   *
   * and `request` is `withAnalyticsArea(params, area)` — the drawn ring is PART OF THE
   * KEY. So completing a draw mints a key nothing has ever written, `data` is null,
   * `isLoading` is true, and this went true again. The skeleton branch replaces the split,
   * the Google Map unmounts, and the instance that replaces it runs its INITIAL fit.
   * That teardown is the FLASH and that first fit is the ZOOM.
   *
   * It also explains the asymmetry the user reported precisely — "removing the draw now
   * works, but SETTING a draw still zooms." Clearing returns to the ORIGINAL key, whose
   * entry is still in the slice (never cleared), so `data` is truthy, no skeleton, no
   * remount. Setting one goes somewhere new. Same code, opposite outcome, which is the
   * signature of a key-dependent test masquerading as a once-only one.
   *
   * The fix is to test what the sentence always MEANT: has this CARD ever successfully
   * loaded. That is a property of the card's lifetime, not of the current request, so it
   * latches once and never goes back — no key can un-latch it.
   *
   * A company with no scans still gets its empty state rather than a permanent skeleton:
   * the first response is a real object (`{data: []}`), so it latches on arrival. That was
   * the original comment's other concern and it still holds.
   */
  const [hasEverLoaded, setHasEverLoaded] = useState(false)
  useEffect(() => {
    if (data && !hasEverLoaded) setHasEverLoaded(true)
  }, [data, hasEverLoaded])
  const showSkeleton = isLoading && !hasEverLoaded

  /**
   * The last successfully-loaded point set, HELD ACROSS A REFETCH.
   *
   * The other half of the draw flash. Even with the map no longer unmounting (above),
   * `data` is null for the whole in-flight window of any new request key — so this was
   * `[]` from the moment a ring was committed until its response landed, and every pin,
   * cluster and list row blinked out and back. On a card whose entire job is a spatial
   * comparison, emptying it mid-gesture is the most disruptive thing it can do, and it
   * happens exactly when the user is watching for the answer.
   *
   * Standard stale-while-revalidate: keep showing what we last knew until we know better.
   * The swap is then one transition (old set -> new set) instead of two (old -> nothing ->
   * new), which is also the only version where the eye can actually SEE what the ring
   * removed.
   *
   * ⚠️ The honest cost: for that window the count beside the list is the PREVIOUS number.
   * It is bounded by one request and self-corrects, and `isLoading` is available to any
   * surface that wants to say so — but it is a real trade and it is the reason this is
   * written down rather than done quietly.
   *
   * The ref only ever advances on a successful response, so it cannot latch onto an error
   * or an in-flight null.
   */
  const lastLoadedPointsRef = useRef<IScanMapPoint[]>([])
  const fetchedPoints = useMemo(() => {
    /*
      ⚠️ THE SUPPRESSED CASE IS CHECKED HERE, NOT ONLY AT `enabled: false`, AND THAT IS THE
      WHOLE SAFETY OF IT.

      `enabled: false` stops this card DISPATCHING, but `data` is not the response — it is
      a read of a REQUEST-KEYED redux slot (`analyticsScansMapKey`). A suppressed pinned
      card drops `scan_statuses` from its payload, so its key collapses onto the key an
      UNPINNED card of the same dimension writes — and if such a card is on the canvas (a
      `Scans by location` beside an `Invalid scans by location` is the obvious arrangement)
      this would read its entry and plot every scan the brand has under a title saying
      "Invalid". Not fetching is not the same as having nothing, and only this test says so.
    */
    if (pinExcludedByFilter) {
      lastLoadedPointsRef.current = []
      return lastLoadedPointsRef.current
    }
    if (data) lastLoadedPointsRef.current = data.data ?? []
    return lastLoadedPointsRef.current
  }, [data, pinExcludedByFilter])

  /**
   * ── SCAN-OUTCOME VISIBILITY ───────────────────────────────────────────────────────
   *
   * Was a card-local `Scan status` DROPDOWN. It is now two separate things, correctly:
   * the toolbar/drawer `Scan status` filter narrows the QUERY (server-side, persisted in
   * a saved view), and this hides outcomes from the view (instant, ephemeral). See
   * `ScansByLocationLegend` for why the legend deliberately does not write the filter.
   *
   * Read from the module store rather than held here, because the legend that writes it
   * renders into the card shell's HEADER and is this component's sibling.
   */
  const hiddenScanStatuses = useHiddenScanStatuses()

  /**
   * Every fetched point's resolved status, computed ONCE per fetch.
   *
   * Keyed off `fetchedPoints` alone, not the selection — `getScanStatus` runs the full
   * resolver per point, and re-running it for thousands of points every time a checkbox
   * moves would make the control feel heavy for no reason. The selection then only
   * filters a precomputed array, and the per-option counts come from the same pass rather
   * than a second one.
   *
   * ⚠️ A plain record, NOT `new Map()`. This module imports `Map` — the shared map
   * COMPONENT from `@repo/ui` — so the global `Map` constructor is shadowed for the whole
   * file. `new Map<string, ScanStatusKey>()` compiles to nonsense here (tsc: "'new'
   * expression, whose target lacks a construct signature"). `globalThis.Map` would work
   * and reads like a workaround for a puzzle; a record is what this actually is.
   */
  const statusByPointId = useMemo(() => {
    const byId: Record<string, ScanStatusKey> = {}
    for (const p of fetchedPoints) byId[p.scan_id] = getScanStatus(scanStatusInput(p)).key
    return byId
  }, [fetchedPoints])

  /**
   * Counts per status across the UNFILTERED set — so the numbers in the menu stay put as
   * you toggle options, instead of every unselected option collapsing to 0 the moment you
   * pick one. A count that changes based on what is already selected cannot answer the
   * question the reader is asking it ("is there anything over there?").
   */
  const scanStatusCounts = useMemo(() => {
    const counts: Partial<Record<ScanStatusKey, number>> = {}
    for (const key of Object.values(statusByPointId)) counts[key] = (counts[key] ?? 0) + 1
    return counts
  }, [statusByPointId])

  /**
   * The card's working set. Pins, the heat surface, the scan list and the count all read
   * from THIS, not from `fetchedPoints` — see `ScansByLocationLegend`'s own note on
   * why the filter narrows the whole card rather than the heat layer alone.
   *
   * For heat specifically this is the entire mechanism: heat's one channel (colour) is
   * already spent encoding density, so status has to narrow what is aggregated rather
   * than tint the result. Filtering BEFORE aggregation means "invalid scans only" renders
   * a true density surface of invalid scans; tinting a blended gradient would render
   * "500 clean" and "40 invalid" identically.
   */
  const points = useMemo(() => {
    if (hiddenScanStatuses.length === 0) return fetchedPoints
    const hidden = new Set<ScanStatusKey>(hiddenScanStatuses)
    return fetchedPoints.filter(p => !hidden.has(statusByPointId[p.scan_id]))
  }, [fetchedPoints, hiddenScanStatuses, statusByPointId])

  /*
    RENDER MODE — pins vs heat. `ScanMapPanel` owns the state (controlled-with-fallback,
    exactly as this card owned it before the extraction) and the deck.gl-unavailable
    handling, so the two props below are a straight pass-through for the day the toggle
    moves into the card header. See the panel for the whole contract.
  */

  /**
   * ── AREA SELECTION (VRG-480) ──────────────────────────────────────────────────────
   *
   * `drawMode` is EPHEMERAL and local: it is which tool is armed, not what is filtered.
   * The filter itself — `filters.area` — is page-global redux, because every card on the
   * page answers to it. Keeping the two apart is what stops "the tool is armed" from
   * being persisted into a saved view, which would restore someone into a map they cannot
   * pan with no memory of why.
   */
  const [drawMode, setDrawMode] = useState<MapDrawMode>('off')
  const area = useSelector(state => state.analytics.filters.area)

  const handleDrawComplete = useCallback(
    (raw: MapRingPoint[]) => {
      // Disarm on completion rather than staying armed for a second stroke. Freehand
      // suppresses panning, so an armed map is a map that does not respond the way a map
      // does — that state has to end at the first opportunity the user's own gesture
      // gives us, not wait for them to find the button again.
      setDrawMode('off')

      /*
        SIMPLIFY BEFORE STORING, never after. A freehand stroke emits several hundred
        vertices; `prepareRing` reduces it below the 50-vertex cap and returns null if it
        cannot (see its header for why null and not a truncated ring — a truncated
        polygon is a DIFFERENT area, silently).

        The reduced ring is what goes into redux, so the ring that is queried, the ring
        that is drawn back onto the map, and the ring that a saved view persists are all
        the same object. Storing the raw gesture and reducing at each boundary would give
        three chances for them to disagree.
      */
      const ring = prepareRing(raw)
      if (!ring) return

      // A redraw that lands on the same shape must not dispatch: every analytics hook
      // keys its refetch on the serialised params, so an equal-but-new ring would refetch
      // the whole page for a filter that did not change.
      if (ringsEqual(ring, area)) return
      dispatch(setAnalyticsFilters({ area: ring }))
    },
    [area, dispatch],
  )

  // Escape, or a gesture too small to enclose anything. Disarms WITHOUT touching the
  // committed area — cancelling a new stroke is not a request to clear the old one.
  const handleDrawCancel = useCallback(() => setDrawMode('off'), [])

  /**
   * ── DO NOT RE-FRAME THE CAMERA ACROSS THE DRAW LIFECYCLE (user-reported TWICE) ─────
   *
   * `useAnalyticsScansMap` merges the page-global `area` into its request
   * (`withAnalyticsArea`), so completing OR clearing a draw re-queries THIS card's own
   * `points` — narrower on complete, back to everything on clear. Either transition
   * changes the shared Map's item set, which is exactly the signal its `fitToItems`
   * effect re-frames on by default. But draw-to-filter is the user narrowing what they
   * already FRAMED, not asking to be shown a different place: re-fitting on complete
   * zoomed the camera in out from under the view they'd just chosen, and re-fitting on
   * clear zoomed back out to the full world — both fight against a gesture whose entire
   * point is "keep looking at what I'm looking at, just filtered."
   *
   * FIRST FIX (removed): a `suppressMapFit` flag, armed in an effect keyed on `area` and
   * consumed in a second effect keyed on `points`, leaning on "React commits child
   * effects before parent effects in the same commit" to land the arm before the
   * consume. That held until the user hit it a SECOND time: `area` and `points` can
   * land in the same commit (clearing the area and the requery resolving together), at
   * which point the arm and the reset raced instead of ordering, and any UNRELATED
   * `points` change between an area edit and the filtered data arriving could consume
   * the flag early too. A flag armed by one effect and consumed by another is a timing
   * window by construction — it does not matter how it is keyed.
   *
   * THIS FIX: `fitSignature` (see `@repo/ui`'s `Map`, `MapProps.fitSignature`) removes
   * the window instead of trying to win it. The map fits only when this STRING changes,
   * full stop — no flag, no "which commit did the reset land in", nothing to race.
   * Built from every input that SHOULD re-frame the camera and — the whole point —
   * `filters.area` is not one of them. Drawing or clearing a ring changes `points`,
   * which changes the ITEM set, but it does not touch this string, so the map's fit
   * effect has no signal to act on: it is structurally incapable of re-framing for it,
   * not merely suppressed for one update.
   *
   * Do NOT reintroduce a suppress-flag here. If a future case needs the map to skip a
   * fit for a reason genuinely outside this signature's inputs, extend the signature
   * (or, if it truly cannot be expressed as "which inputs changed", use `suppressFit`
   * on the ONE call site that needs it — see that prop's doc for why it is the fallback
   * and not the default).
   */
  const fitSignature = useMemo(
    () =>
      JSON.stringify([
        filters.dateRange.from,
        filters.dateRange.to,
        filters.ownerIds,
        filters.productIds,
        filters.certificateIds,
        filters.location,
        filters.certificateStatuses,
        /*
          The scan-status filter BELONGS in the signature, unlike `filters.area` which is
          deliberately absent above.

          The distinction this list encodes is not "page filter vs card filter" — it is
          whether a change means SHOW ME A DIFFERENT SET (refit) or KEEP LOOKING HERE,
          FILTERED (do not). Drawing a ring is the second: it narrows within the view the
          reader is already looking at, so re-framing fights them. Picking a scan status is
          the first. ⚠️ The LEGEND is deliberately absent for the same reason `area` is:
          hiding an outcome is "keep looking here, with less drawn", not "show me a
          different set". Re-framing on a legend click would move the map out from under
          the comparison the click was made to perform.

          `filters.scanStatuses` (the toolbar filter) is a genuine set change and reaches
          this signature through the request it drives, not through this list.
        */
        effectiveMode,
        // The card's own dimension, not just the certificates boolean — `invalid_scans`
        // and `scans` are two different SETS at the same `isCertificates`, and switching
        // between them (the legacy card cannot, but two variants on one canvas both
        // re-render through this) is a "show me a different set" change, so it refits.
        dimension,
      ]),
    [
      filters.dateRange.from,
      filters.dateRange.to,
      filters.ownerIds,
      filters.productIds,
      filters.certificateIds,
      filters.location,
      filters.certificateStatuses,
      effectiveMode,
      dimension,
    ],
  )

  /**
   * VRG-491 — `clusterPriority` / `clusterColor` are passed NOW, and their absence was a
   * real defect rather than a gap.
   *
   * This card has always set `enableClustering`, and the shared `Map` tints a cluster with
   * its highest-`clusterPriority` member's `clusterColor` — so with neither supplied every
   * cluster rendered in the neutral default, and an invalid scan stacked with four verified
   * ones was invisible at anything but full zoom. The certificate page had the exact
   * mirror-image bug: it COMPUTED both fields and then passed `enableClustering={false}`,
   * so its severity ladder was dead code. Each surface held one half of the feature, which
   * is what happens when two files implement one picture (§36).
   *
   * `status.severity`, not a hand-rolled ladder: `tampered` is 0 (equal to verified,
   * because a broken seal is the expected outcome of a closure tag being opened) and
   * `invalid` is 3. Only an outcome that outranks routine contributes an accent — passing
   * one at severity 0 would be inert anyway, since the cluster renderer requires
   * `priority > 0` before a member's colour can win.
   */
  const mapItems: MapItem[] = useMemo(
    () =>
      points.map(point => {
        const status = getScanStatus(scanStatusInput(point), {isDark})
        return {
          id: point.scan_id,
          lat: point.geo_lat,
          lng: point.geo_lng,
          fillColor: status.color,
          /*
            ── WHICH MEMBER OF A STACK SPEAKS FOR IT ──────────────────────────────────

            `severity` is `ScanStatusMeta`'s own answer to this question — its docstring
            says so in as many words: "used to pick which member of a STACK the pin
            colours itself by … or a clean scan sitting first in the array would hide an
            invalid one underneath it." This card was not passing it, so `Map`'s `lead`
            reduce compared `undefined ?? 0` against `undefined ?? 0` for every member,
            never took the `>` branch, and returned the FIRST element of the group. The
            stack then spoke for whichever scan the query happened to return first.

            That was invisible while stacks painted in `DEFAULT_PIN_COLOR` regardless
            (the bug fixed alongside this in `Map.tsx`); the moment a stack takes its
            lead's colour, an unranked lead means the colour is arbitrary rather than
            worst-case. The two halves only work together.

            `clusterColor` is the CLUSTERER's parallel channel and is set only for a
            non-clean outcome, matching `Scans.tsx`: `createClusterSvg` falls back to the
            calm default when no member passes one up, so handing it a purple for a clean
            scan would say "accented" about a cluster that has nothing to accent.
          */
          clusterPriority: status.severity,
          clusterColor: status.severity > 0 ? status.color : undefined,
          /*
            ⚠️ `status`, not `status.key` — main reached the same clustering fix
            independently and its call passed the key, because on main
            `buildScanTooltip` still took one. This branch widened that signature to the
            whole `ScanStatusMeta` (`caf8997a2`) so the tooltip can read `chipDark` for
            the injected-HTML info window. Taking main's line wholesale would not have
            type-checked; taking ours would have dropped the comment above, which is the
            only place the `lead`-reduce bug is explained. Both halves survive.
          */
          tooltipHtml: buildScanTooltip(point, status),
        }
      }),
    [points, isDark],
  )

  // A selection is only meaningful against the rows it was made on. When the filters
  // or the mode change the response is a different set of scans, so a held id would
  // highlight nothing while still suppressing the "nothing selected" state.
  useEffect(() => {
    setActiveId(undefined)
  }, [points])

  /*
    Selection-scroll sync, the width `ResizeObserver`, `relayoutNonce`, and the deliberate
    absence of a remount `key` on `<Map>` all live in `ScanMapPanel` now. That block was
    the most expensively-earned code in this file — six reported bugs' worth of "the map
    flashes and zooms" — and the certificate page had none of it, so its map was torn down
    and rebuilt on every resize-drag release. Moving it is what makes that stop being true
    on one surface only. The reasoning went with it; read it there before changing any of
    it.
  */

  /**
   * Empty copy. It must not assert a fact this surface cannot check.
   *
   * The previous resolver branched on `mapItems.length === 0 && points.length > 0` to
   * say "these scans have no location". That branch was UNREACHABLE, twice over:
   * `mapItems` is a 1:1 `points.map(...)`, so the two lengths are always equal, and
   * upstream the SQL already filters `geo_lat IS NOT NULL AND geo_lng IS NOT NULL`
   * inside the CTE, so an unlocated scan never reaches the client at all. The
   * distinction it was written to preserve had been erased server-side.
   *
   * So the only reachable case is an empty payload, and it has TWO causes we cannot
   * tell apart from here: no scans in the window, or scans in the window that all
   * declined a location. "No scans recorded yet" claims the first, and is a lie in the
   * second — the exact failure the old comment set out to fix. The wording below is
   * true under both, and names the location caveat rather than implying there was no
   * activity.
   */
  const emptyCopy = useMemo(() => {
    /*
      The suppressed case gets its OWN sentence rather than falling back to "no invalid
      scans in this range". That fallback would be a claim about the DATA, and the truth
      here is a claim about the CONTROLS: the page's Scan status filter and this card's own
      dimension select disjoint sets, so nothing was even asked for. Telling a reader their
      brand has no invalid scans, when the reader is one click from seeing 245 of them, is
      the plausible-looking falsehood this whole surface is built to refuse.
    */
    if (pinExcludedByFilter) {
      return {
        title: 'Nothing to show for this Scan status filter',
        text: 'This card shows invalid scans, and the Scan status filter above excludes them. Clear the filter or include Invalid to see them here.',
      }
    }
    if (isCertificates) {
      return {
        title: 'No certificate locations in this range',
        text: 'A certificate appears here once it has been scanned by a visitor who shared their location. Adjust the date range or filters above.',
      }
    }
    if (dimension === 'invalid_scans') {
      return {
        title: 'No located invalid scans in this range',
        text: 'A rejected scan appears here only when the visitor shared a location and the scan matched one of your certificates. Adjust the date range or filters above.',
      }
    }
    return {
      title: 'No located scans in this range',
      text: 'Scans appear here only when the visitor shares their location. Adjust the date range or filters above.',
    }
  }, [isCertificates, dimension, pinExcludedByFilter])

  // The title alone. It used to append "Visitors choose whether to share a location." —
  // true, and the reason the map can be empty while scans exist, but it doubled the
  // overlay's height for something the word LOCATED already implies. The full
  // explanation still reaches anyone who needs it: `emptyCopy.text` carries it into the
  // list pane's empty state beside this map.
  const mapEmptyMessage = `${emptyCopy.title}.`

  const listNoun = isCertificates ? 'certificate' : 'scan'

  return (
    /*
     * BODY ONLY — no `Card`, no `CardSectionHeader`.
     *
     * `AnalyticsCardShell` draws the surface and the header for this panel
     * (`surface: 'shell'`), so drawing either here would produce a card inside a card.
     * The title and description are exported as `scansByLocationTitle` /
     * `scansByLocationDescription` and the mode control as `ScansByLocationModeToggle`,
     * which the page passes to the shell's header action slot — which is also why `mode`
     * is a prop rather than local state.
     *
     * VRG-491 — the split itself is `ScanMapPanel`, shared with the certificate page's
     * Scans tab. What stays here is what is genuinely this card's: which points to ask
     * for, what a row looks like, what the empty copy claims, and the area-draw filter
     * that writes page-global redux.
     */
    <ScanMapPanel
      id={SCANS_MAP_ANCHOR_ID}
      items={mapItems}
      activeId={activeId}
      onActiveIdChange={setActiveId}
      count={points.length}
      noun={listNoun}
      apiKey={GOOGLE_MAPS_API_KEY || ''}
      // Expanded, the card owns the whole content region, so the split fills whatever
      // height the shell gives it rather than staying at its own 600px. Otherwise the
      // fixed height is what keeps the two panels the same size in the grid.
      //
      // `expanded` still means exactly one thing — "this card owns the whole content
      // region" — and is unrelated to the list's collapsed state. They are different axes:
      // one is how much of the PAGE this card gets, the other is how that card divides
      // itself.
      height={expanded ? '100%' : SCAN_MAP_PANEL_HEIGHT}
      // First load only — see `showSkeleton`.
      loading={showSkeleton}
      emptyMessage={mapEmptyMessage}
      // See the `fitSignature` block above — the map re-frames only when this string
      // changes, and it deliberately excludes `filters.area`, so a draw-complete /
      // draw-clear item-set change cannot trigger a fit.
      fitSignature={fitSignature}
      /*
        A STANDING VETO WHILE A RING IS APPLIED — belt to `fitSignature`'s braces.

        `fitSignature` stops a re-fit by remembering it already fitted for this signature,
        but that memory lives in the map INSTANCE. Anything that recreates the instance
        resets it, and the next run then treats the current signature as new and re-frames
        around whatever pins the ring left visible — exactly the "setting a draw still
        zooms in" report. A signature can prevent a RE-fit; it cannot prevent a FIRST fit.
        This can: while `area` is set, no instance re-frames, however fresh it is.
      */
      suppressFit={Boolean(area)}
      renderMode={renderModeProp}
      onRenderModeChange={onRenderModeChange}
      /*
        ⚠️ `area` is rendered in EVERY mode, armed or not. A saved view that restores an
        area filter without putting the shape back leaves the reader looking at narrowed
        numbers with no visible cause — worse than not persisting it at all, because the
        filtering is invisible rather than merely absent. The shape is part of the view's
        visible state, not just its query.
      */
      drawMode={drawMode}
      drawnRing={area}
      onDrawComplete={handleDrawComplete}
      onDrawCancel={handleDrawCancel}
      // On the tiles unless the page has taken it into the card header — see
      // `drawControlInHeader`.
      canvasTopLeft={
        drawControlInHeader ? undefined : (
          <ScansByLocationDrawControl variant="canvas" value={drawMode} onChange={setDrawMode} />
        )
      }
      list={
        /* LIST COHERENCE: the list and the map render the SAME `points` array, so whatever
           dedupe the query applied is already applied to both. In certificates mode the
           payload is one row per certificate, so the list is one row per certificate too —
           it cannot fall out of step with the pins, because there is no second query.
           `perCertificate` only changes how a row LABELS its timestamp, since the same row
           now means "last seen here" rather than "scanned here". */
        <ScanList
          points={points}
          activeId={activeId}
          onItemClick={setActiveId}
          perCertificate={isCertificates}
          emptyTitle={emptyCopy.title}
          emptyText={emptyCopy.text}
        />
      }
    />
  )
}
